import { z } from 'zod';
/**
 * Payments contract (Phase 13 — SCAFFOLD ONLY).
 *
 * ⛔ NO money-moving code lives in Phase 13. See
 * `docs/adr/ADR-payments-custody.md` (STATUS: PROPOSED — BLOCKED ON USER
 * LEGAL DECISION). These are the shapes an eventual PSP integration will use;
 * the only implementation that reads/writes them today is the deterministic
 * in-memory MockPaymentProvider + the append-only ledger, both gated behind
 * `PAYMENTS_ENABLED` (default false).
 *
 * The ledger is APPEND-ONLY: entries are never updated or deleted. A user's
 * deposit-backed `biddingPower` is a pure fold over their ledger entries (see
 * {@link computeBiddingPowerFromLedger}) — the balance invariant Phase 13
 * tests assert.
 */
/** Currencies the platform reasons about (CHF auction leg, EUR/UAH/USD client legs). */
export const PaymentCurrencySchema = z.enum(['CHF', 'EUR', 'USD', 'UAH']);
/**
 * How a converted figure got its value, carried beside the converted figure.
 *
 * A `*Chf` field may hold a number that started life in another currency ONLY
 * when this rides along. Without it a stored CHF amount cannot be reproduced,
 * reconciled against the figure it came from, or explained to a client who
 * disputes it — and nothing on the wire even records that a conversion
 * happened. The CarsFinders service fee is the case that forces this today:
 * `computeClientDebt` prices it in EUR, and the invoice is CHF.
 *
 * `rate` is the multiplier ACTUALLY applied — `valueChf ≈ fromAmount × rate` —
 * snapshotted at conversion time rather than looked up again at render time,
 * and `ratesVersion` is the `PricingRules` version it came from (e.g.
 * `ua-2026-02`, `customs.version`) — the same immutable rule-set document that
 * supplies `swissExport.chfToEurRate`. For a EUR→CHF conversion `rate` is
 * therefore the RECIPROCAL of that rule-set figure (EUR per CHF), because the
 * multiplier applied to a EUR amount is CHF per EUR; {@link convertEurToChf}
 * is the one place that inversion happens. Pricing rules are rates-as-data and
 * a new rate is a NEW version, so the rate can move under an invoice that has
 * already been rendered; that is exactly why the one that was used is stored
 * with the figure it produced.
 */
export const FxConversionSchema = z.object({
    /** The currency the figure was converted FROM. */
    fromCurrency: PaymentCurrencySchema,
    /** The pre-conversion magnitude, in `fromCurrency`. */
    fromAmount: z.number().finite().nonnegative(),
    /** Multiplier applied: converted ≈ `fromAmount × rate`, rounding is the caller's. */
    rate: z.number().finite().positive(),
    /** `PricingRules.version` that supplied `rate`, e.g. `ua-2026-02`. */
    ratesVersion: z.string(),
});
/**
 * A client payment as the operator recorded it, in its ORIGINAL currency
 * (admin «Додати платіж», client-finances-rethink): the whole payment, not the
 * part one trace carries. `fx` is how it was converted to CHF (null for CHF).
 * Stamped on every trace of the payment (ledger row, invoice settlement) so the
 * journal can show it without reading the outbox.
 */
export const PaymentOriginalSchema = z.object({
    amount: z.number().finite().positive(),
    currency: PaymentCurrencySchema,
    fx: FxConversionSchema.nullable(),
});
/**
 * Ledger entry kind. `deposit` (the design's «top_up») credits biddingPower,
 * `refund` and `fee` debit it, `prepayment` debits it (deposit applied to an
 * invoice — carries `invoiceId`), and `adjustment` is an admin correction whose
 * sign is its `direction` (`credit` +, `debit` −).
 *
 * `penalty` debits it too (owner decision (a), 2026-09-28): the deposit a client
 * FORFEITS when they refuse to pay for a won car, written by the admin-confirmed
 * `POST /api/admin/deals/:dealId/forfeit-deposit` under the idempotency key
 * `deal:<dealId>:forfeit` (`deal-forfeit.ts`). Appended LAST so every existing
 * value keeps its position.
 */
export const PaymentEntryTypeSchema = z.enum([
    'deposit',
    'refund',
    'fee',
    'prepayment',
    'adjustment',
    'penalty',
]);
/** Sign of an `adjustment` entry. Required on an adjustment, forbidden elsewhere. */
export const LedgerDirectionSchema = z.enum(['credit', 'debit']);
/**
 * How the money actually moved for a ledger entry: bank transfer, card (an
 * admin's manual entry for an office terminal, or a PSP webhook behind
 * `PAYMENTS_ENABLED`), or cash at the office. Declared here — not in
 * `deposit-topup.ts`, which re-exports it — so the ledger schema carries no
 * import cycle. Rows written before this field existed simply have no method.
 */
export const LedgerMethodSchema = z.enum(['bank', 'card', 'cash']);
/**
 * PaymentStatus — the lifecycle of a single payment intent as a PSP would
 * report it. Terminal: `credited`, `failed`, `refunded`. Scaffold only — no
 * transition engine ships in Phase 13; the ledger records the outcome
 * (`credited`/`refunded`) as a `PaymentEntryType`, not this status.
 */
export const PaymentStatusSchema = z.enum([
    'pending', // checkout created, awaiting the client
    'authorized', // PSP authorized, funds not yet captured
    'credited', // captured + credited to the ledger (terminal, happy path)
    'failed', // PSP declined / abandoned (terminal)
    'refunded', // fully refunded (terminal)
]);
export const PAYMENT_STATUSES = PaymentStatusSchema.options;
/**
 * One append-only ledger entry (`payments` collection).
 *
 * `providerRef` is the PSP's idempotency key — a UNIQUE index enforces that
 * the same provider reference is never credited twice (see the payments
 * module's idempotency guard + unique index on `providerRef`).
 */
export const PaymentLedgerEntrySchema = z
    .object({
    userId: z.string(),
    type: PaymentEntryTypeSchema,
    /** Positive magnitude in `currency`; `type` (and `direction`) decide the sign of its effect. */
    amount: z.number().nonnegative(),
    currency: PaymentCurrencySchema,
    /** PSP idempotency reference; unique across the ledger. */
    providerRef: z.string(),
    createdAt: z.coerce.date(),
    /** The invoice a `prepayment` was applied to. */
    invoiceId: z.string().optional(),
    /** Sign of an `adjustment` — required iff `type === 'adjustment'`. */
    direction: LedgerDirectionSchema.optional(),
    /** Admin userId who wrote an admin-initiated entry (adjustment, refund, top-up). */
    actorId: z.string().optional(),
    /** Free-text reason the admin gave. */
    note: z.string().optional(),
    /** How the money moved (additive; legacy rows have none and render without a method suffix). */
    method: LedgerMethodSchema.optional(),
    /** Client-facing payment reference, e.g. a top-up request's `DEP-AT-0927`. */
    reference: z.string().max(64).optional(),
    /** Cash-desk receipt number (or bank statement ref) the manager recorded. */
    receiptRef: z.string().max(64).optional(),
    /** The `deposit_topup_requests` id this entry settled, when it came from a top-up request. */
    topUpRequestId: z.string().optional(),
    /**
     * How `amount` was reached when it was converted from a tariff priced in
     * another currency — e.g. the Dealer Pro subscription, whose `monthlyFee`
     * is a EUR tariff charged in CHF at the active rule set's rate. Absent on
     * every row whose amount is native to `currency`.
     */
    fx: FxConversionSchema.optional(),
    /**
     * The admin client payment (`POST /api/admin/users/:userId/payments`) this
     * row is one allocation of — the key the finance journal groups by. Absent
     * on every row not written by that endpoint.
     */
    paymentId: z.string().uuid().optional(),
    /** When the client payment was received (the operator's date); absent elsewhere. */
    receivedAt: z.coerce.date().optional(),
    /** The WHOLE client payment in its original currency; absent elsewhere. */
    original: PaymentOriginalSchema.optional(),
})
    .superRefine((entry, ctx) => {
    if (entry.type === 'adjustment' && entry.direction === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['direction'],
            message: 'direction is required on an adjustment entry',
        });
    }
    if (entry.type !== 'adjustment' && entry.direction !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['direction'],
            message: 'direction is only allowed on an adjustment entry',
        });
    }
});
/**
 * Copart-model coefficient linking a client's deposit balance to their bid
 * ceiling: `maxBid = biddingPower × 10` (mirrors BidsService.checkBiddingPower
 * and the relay deposit gate). A deposit of X credits X of biddingPower, so
 * `sum(deposits) − sum(refunds+fees) === biddingPower` — the ledger invariant.
 */
export const BIDDING_POWER_MAX_BID_COEF = 10;
/**
 * Signed effect of a ledger entry on biddingPower: deposits and credit
 * adjustments credit (+); refunds, fees, prepayments, penalties and debit
 * adjustments debit (−). Pure. An adjustment WITHOUT a direction (which the schema
 * refuses) is read as a debit — understating buying power is the safe way to
 * be wrong.
 */
export function ledgerEntrySignedAmount(entry) {
    if (entry.type === 'deposit')
        return entry.amount;
    if (entry.type === 'adjustment' && entry.direction === 'credit')
        return entry.amount;
    return -entry.amount;
}
/**
 * Fold a user's append-only ledger into their deposit-backed biddingPower.
 * Pure + deterministic: the sum of {@link ledgerEntrySignedAmount} — deposits
 * and credit adjustments minus refunds, fees, prepayments and debit
 * adjustments. Callers must project `direction` for adjustments to fold
 * correctly. This is
 * the balance invariant the Phase 13 ledger tests assert against the value
 * written to `users.biddingPower`.
 */
export function computeBiddingPowerFromLedger(entries) {
    return entries.reduce((sum, entry) => sum + ledgerEntrySignedAmount(entry), 0);
}
/**
 * `deposit_credited` outbox event — appended in the SAME transaction as the
 * ledger insert + biddingPower increment (same transactional-outbox guarantee
 * as the relay desk). Consumed to fan out a "deposit credited" notification.
 */
export const DepositCreditedEventSchema = z.object({
    type: z.literal('payment.deposit_credited'),
    data: z.object({
        userId: z.string(),
        amount: z.number().nonnegative(),
        currency: PaymentCurrencySchema,
        providerRef: z.string(),
        /** biddingPower AFTER applying this deposit. */
        newBiddingPower: z.number(),
        creditedAt: z.string(), // ISO timestamp
    }),
});
//# sourceMappingURL=payment.js.map